'use client';

import { Slot } from '@radix-ui/react-slot';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { PropertyCardRootContext } from './context/property-card-root.context';
import { propertyCardDataAreaRecipe } from './styles/property-card-data-area.styles';
import type { PropertyCardDataAreaPropsInterface } from './types/property-card-data-area.types';

/**
 * Used for displaying a line of property data in a `PropertyCard`.
 *
 * @since 8.63.0
 */
export const PropertyCardDataArea = forwardRef<HTMLDivElement, PropertyCardDataAreaPropsInterface>(
  (props: PropertyCardDataAreaPropsInterface, forwardedRef) => {
    const propertyCardRootContext = useContext(PropertyCardRootContext);
    const {
      asChild = false,
      appearance = propertyCardRootContext.appearance || 'large',
      children,
      className,
      css: cssProp,
      dataType = 'dataArea1',
      lineClamp = 1,
      loading = propertyCardRootContext.loading || false,
      ...rest
    } = props;

    const styles = css(
      { lineClamp },
      propertyCardDataAreaRecipe.raw({ appearance, dataType, loading }),
      cssProp,
    );

    const Component = asChild ? Slot : 'div';

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="PropertyCard.DataArea"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_property-card__data-area', styles, className)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

PropertyCardDataArea.displayName = 'PropertyCard.DataArea';
